import axios from 'axios';
import store, { state } from './store/store';
import { HttpStatus } from './constants/httpStatus';
import { ForbiddenReason } from './constants/customErrorCodes';

const isExternalRequest = ({ url = '' } = {}) => url.indexOf('http') === 0 && window.location.origin !== new URL(url, document.baseURI).origin;

const masqueradeAuthEndpointRegex = /\/(?:admin\/(?:endM|m)asquerade|current\/session)/;

/**
 * Allows us to simultaneously support both JWT- and session-based auth for masquerading by adding the JWT to the Authorization header.
 * We only do this for specific endpoints related to masquerading.
 * This is a temporary solution for the sake of transitioning to the React application.
 */
function masqueradeAuthInterceptor(config) {
    if (isExternalRequest(config) || !masqueradeAuthEndpointRegex.test(config.url) || !state.auth) return config;
    return {
        ...config,
        headers: {
            ...config.headers,
            Authorization: `Bearer ${state.auth.access_token}`,
            'X-Commit-Session': true,
        },
    };
}

function currentLanguageInterceptor(config) {
    if (isExternalRequest(config) || !state.i18n?.locale) return config;
    return {
        ...config,
        headers: {
            ...config.headers,
            'Accept-Language': state.i18n?.locale,
        },
    };
}

const stickyHeaderEndpointWhitelist = /\/((current|session)\/(user(?!\/shops)|static|stickySessionInfo)|auth\/rest\/refresh|shop\/shopDetails)/;

/**
 * Adds an 'x-sticky' header to all requests when authenticated to ensure requests go to a single pod in k8s
 * Without this, responses might include cached (and possibly incorrect) data from a different pod
 */
async function stickySessionInterceptor(config) {
    if (isExternalRequest(config)) return config;

    if (!state.stickySessionId) {
        // Need to first check for a handful of url's that we can't/don't want to even try to add x-sticky to because they will cause infinite awaiting issues
        if (stickyHeaderEndpointWhitelist.test(config.url) || !store.getters.isAuthorizedUser) return config;
        await store.dispatch('requestIfIdle', ['getStickySessionId']);
    }

    // If something goes awry with the sticky session request, don't attach the header
    if (!state.stickySessionId) return config;

    return {
        ...config,
        headers: {
            ...config.headers,
            'x-sticky': state.stickySessionId,
        },
    };
}

async function masqueradeAuthErrorInterceptor(error) {
    // Only handle unauthorized requests to our own API
    if (isExternalRequest(error.config) || error.response?.status !== HttpStatus.UNAUTHORIZED) return Promise.reject(error);

    const isExpiredMasqueradeAuthToken = !!state.auth && masqueradeAuthEndpointRegex.test(error.config?.url);
    let responseError = error;

    if (isExpiredMasqueradeAuthToken) {
        // Avoid an infinite loop. Did we already retry the reqeuest?
        // Make sure this error isn't the result of an attempt to retry the request.
        const isRefreshTokenRequest = error.config?.url.includes('/auth/rest/refresh');
        const isRetryRequest = !!error.config?.isRetry;
        if (!isRefreshTokenRequest && !isRetryRequest) {
            try {
                await store.dispatch('refreshAuth');

                // With our refreshed access token in hand, let's try the request again.
                return axios({
                    ...error.config,
                    headers: {
                        ...error.config?.headers,
                        Authorization: `Bearer ${state.auth.access_token}`,
                    },
                    isRetry: true,
                });
            } catch (retryError) {
                responseError = retryError;
            }
        }
    }

    return Promise.reject(responseError);
}

/**
 * Checks every 403 error response to see if the user has restrictions placed on them for accepting terms or resetting their password.
 * The base app component can then respond to changes in these values and redirect to the corresponding page so the user can update their info.
 */
async function forbiddenErrorInterceptor(error) {
    // Only handle forbidden requests to our own API
    if (isExternalRequest(error.config) || error.response?.status !== HttpStatus.FORBIDDEN) return Promise.reject(error);

    const errorId = error.response?.data?.errorId;

    if (errorId === ForbiddenReason.ACCEPT_TERMS_REQUIRED) {
        store.commit('setTermsAcceptRequired', true);
    } else if (errorId === ForbiddenReason.PASSWORD_RESET_REQUIRED) {
        store.commit('setPasswordChangeRequired', true);
    }

    return Promise.reject(error);
}
/**
 * If a user is considered authenticated on the FE but their requests are getting redirected to login.html, it means their session is invalid.
 * Handles the scenario where, in the legacy (non-SPA) version, the user can be logged out but remain on the page while trying to perform actions.
 * In these cases, it can be very confusing to the user why all their attempts are failing because they get a generic error.
 * Instead, if they are authenticated on the FE but unauthenticated on the BE, log them out on the FE only.
 * The watcher in BaseApp.vue will be triggered on auth state change and perform the redirect.
 */
function legacyUnauthorizedRedirectInterceptor(response) {
    if (!state.isSPA) {
        const {
            request: { responseURL },
        } = response;
        if (state.user?.authenticated && responseURL.includes('login.html')) {
            store.dispatch('logout');
        }
    }
    return response;
}

axios.interceptors.request.use(masqueradeAuthInterceptor);
axios.interceptors.request.use(currentLanguageInterceptor);
axios.interceptors.request.use(stickySessionInterceptor);
axios.interceptors.response.use((response) => response, masqueradeAuthErrorInterceptor);
axios.interceptors.response.use((response) => response, forbiddenErrorInterceptor);
axios.interceptors.response.use(legacyUnauthorizedRedirectInterceptor);

axios.defaults.baseURL = __FCO_API_URL__;
